import React, { useEffect, useRef, useState } from 'react';
import YouTubeEmbed, { isYouTubeUrl } from './YouTubeEmbed';

const isMobileNow = () => typeof window !== 'undefined' && window.innerWidth <= 860;

/**
 * LazyVideo — performance-first video player used across the homepage.
 *  • preload="none" + mounts the <video> only when scrolled near the viewport
 *  • Desktop (default): autoplay muted while on screen, pauses off screen, tap toggles sound
 *  • Mobile / tapToPlay: shows the poster until the user taps — then plays with sound
 *  • If `src` is a YouTube URL, hands off to <YouTubeEmbed> which renders a
 *    brand-free autoplay+loop embed with all click-through disabled.
 */
export default function LazyVideo({
  src, poster, fill = false, aspect = '16/9',
  tapToPlay = false, className = '', style = {}, testId = 'lazy-video',
}) {
  // YouTube fast path — no <video> element, no autoplay bookkeeping.
  if (isYouTubeUrl(src)) {
    return <YouTubeEmbed src={src} poster={poster} aspect={aspect} fill={fill} className={className} style={style} testId={testId} />;
  }
  return <NativeLazyVideo src={src} poster={poster} fill={fill} aspect={aspect} tapToPlay={tapToPlay} className={className} style={style} testId={testId} />;
}

function NativeLazyVideo({
  src, poster, fill, aspect,
  tapToPlay, className, style, testId,
}) {
  const wrapRef = useRef(null);
  const [videoEl, setVideoEl] = useState(null);
  const [inView, setInView] = useState(false);
  const [nearView, setNearView] = useState(false);
  const [started, setStarted] = useState(false);
  const [muted, setMuted] = useState(true);
  const [mobile] = useState(isMobileNow);

  // Autoplay-muted mode ("passive") is enabled EVERYWHERE except when the
  // caller explicitly opts into tap-to-play (used by 9:16 reels only). Mobile
  // browsers happily play muted videos inline so we no longer force
  // tap-to-play on small viewports.
  const passive = !tapToPlay;

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.25 });
    const near = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setNearView(true); near.disconnect(); } }, { rootMargin: '400px' });
    io.observe(el); near.observe(el);
    return () => { io.disconnect(); near.disconnect(); };
  }, []);

  useEffect(() => {
    const v = videoEl;
    if (!v) return;
    const wants = passive ? inView : (started && inView);
    if (wants) v.play().catch(() => {});
    else v.pause();
  }, [inView, passive, started, videoEl]);

  const handleClick = () => {
    if (passive) { setMuted(m => !m); return; }
    if (!started) { setMuted(false); setStarted(true); }
    else {
      const v = videoEl;
      if (v) { if (v.paused) v.play().catch(() => {}); else v.pause(); }
    }
  };

  const mountVideo = src && (passive ? nearView : started);
  const videoVisible = passive ? true : started;

  return (
    <div
      ref={wrapRef}
      className={`lazyvid ${className}`}
      style={fill
        ? { position: 'absolute', inset: 0, overflow: 'hidden', cursor: 'pointer', ...style }
        : { position: 'relative', aspectRatio: aspect.replace('/', ' / '), overflow: 'hidden', cursor: 'pointer', ...style }}
      onClick={handleClick}
      data-testid={testId}
    >
      {poster && !videoVisible && (
        <img src={poster} alt="" loading="lazy" decoding="async"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', background: '#0e0b08' }} />
      )}
      {mountVideo && (
        <video
          ref={setVideoEl}
          src={src}
          poster={poster || undefined}
          muted={muted}
          loop
          playsInline
          preload={fill ? 'metadata' : 'none'}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', background: '#0e0b08' }}
          data-testid={`${testId}-el`}
        />
      )}
      {!passive && !started && (
        <span className="lv-play" aria-label="Play video" data-testid={`${testId}-play`} />
      )}
      {passive && src && (
        <span className="lv-sound" data-testid={`${testId}-sound`}>{muted ? 'Tap for sound' : 'Sound on'}</span>
      )}
    </div>
  );
}
